<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import type {
  CssCustomPropertyFont,
  CssCustomPropertyColor,
} from '../../helpers/cssCustomProperties';
import {
  getStringStyles,
  getCssCustomPropertyValue,
} from '../../helpers/cssCustomProperties';

interface AppRouterLinkProps {
  link: string;
  font?: CssCustomPropertyFont;
  color?: CssCustomPropertyColor;
  wide?: boolean;
}

const props = defineProps<AppRouterLinkProps>();

interface AppRouterLinkEmits {
  // NOTE: This event is meant to be used with telemetry events when navigation is triggered
  (eventName: 'navigation-triggered'): void;
  (eventName: 'capture-click'): void;
}

const emit = defineEmits<AppRouterLinkEmits>();

const style = computed(() =>
  getStringStyles(props.color, getCssCustomPropertyValue)
);

function onClick(event: MouseEvent): void {
  event.stopPropagation();
  emit('navigation-triggered');
}

function onCaptureClick(): void {
  emit('capture-click');
}
</script>

<template>
  <!-- In some cases, we need to execute a function before navigating to a route.
   That’s why this `click.capture` has been added—specifically for the redirect of *ownStay*,
   as we need to add an accommodation ingredient before navigating to the hotel selection.-->

  <RouterLink
    class="app-router-link"
    :class="{
      [props.font as string]: !!props.font,
      wide: props.wide,
    }"
    :to="props.link"
    :style="style"
    @click="onClick"
    @click.capture="onCaptureClick"
  >
    <slot />
  </RouterLink>
</template>

<style scoped lang="scss">
@use '../../styles/media' as *;
@use '../../styles/fonts' as *;

.app-router-link {
  display: inline-block;
  text-decoration: none;

  @include on-hover {
    cursor: pointer;
    text-decoration: underline;
  }

  &.wide {
    display: grid;
    width: 100%;
  }
}
</style>
